import * as Sentry from '@sentry/vue';

import { getWellnessCloudOrigin, toWellnessCloudRefreshUrl } from '~/utils/wellnesscloud';

// リフレッシュトークンのアイドル時有効期間(30分)より十分短くし、本アプリの操作が続く限り委譲先を失効させない
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// 更新の途中でiframeを外すと、ローテーション済みのトークンを委譲先が保存できず、再利用検知でトークンの系統ごと失効する。
// そのため応答を待つ時間は長めに取る
const REFRESH_TIMEOUT_MS = 30 * 1000;

// 委譲先の状態はタブ間で共通なので、タブ間で共有する。
// メモリ上に持つと、本アプリをタブの数だけ開いたユーザーはタブの数だけ更新する
const LAST_REFRESHED_AT_STORAGE_KEY = 'wellnesscloud_last_refreshed_at';
const REFRESH_SUSPENDED_STORAGE_KEY = 'wellnesscloud_refresh_suspended';
const USER_MISMATCH_STORAGE_KEY = 'wellnesscloud_user_mismatch';

const REFRESH_MESSAGE_TYPE = 'auth-refresh';

export type WellnessCloudRefreshResult =
  | { status: 'success'; sub: string | null }
  | { status: 'failure' }
  | { status: 'timeout' };

let pendingRefresh: Promise<WellnessCloudRefreshResult> | null = null;

export const markWellnessCloudRefreshed = (now: number = Date.now()): void => {
  localStorage.setItem(LAST_REFRESHED_AT_STORAGE_KEY, String(now));
};

export const isWellnessCloudRefreshDue = (now: number = Date.now()): boolean => {
  if (localStorage.getItem(REFRESH_SUSPENDED_STORAGE_KEY) === 'true') {
    return false;
  }
  const lastRefreshedAt = Number(localStorage.getItem(LAST_REFRESHED_AT_STORAGE_KEY));
  if (!lastRefreshedAt) {
    return true;
  }
  return now - lastRefreshedAt >= REFRESH_INTERVAL_MS;
};

const toRefreshResult = (
  event: Pick<MessageEvent, 'origin' | 'source' | 'data'>,
  iframeWindow: Window | null,
): WellnessCloudRefreshResult | null => {
  if (!iframeWindow || event.source !== iframeWindow || event.origin !== getWellnessCloudOrigin()) {
    return null;
  }
  const { data } = event;
  if (!data || data.type !== REFRESH_MESSAGE_TYPE || typeof data.ok !== 'boolean') {
    return null;
  }
  if (!data.ok) {
    return { status: 'failure' };
  }
  return { status: 'success', sub: typeof data.sub === 'string' ? data.sub : null };
};

const refreshInIframe = (): Promise<WellnessCloudRefreshResult> => new Promise((resolve) => {
  const iframe = document.createElement('iframe');
  iframe.src = toWellnessCloudRefreshUrl();
  iframe.title = 'WellnessCloudセッション更新';
  // 委譲先は別オリジンなので、allow-same-originを付けても親側のsandbox属性は外せない。
  // トップレベルの遷移、ポップアップ、フォーム送信を防ぐ制限として効く
  iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
  // referrerpolicy="no-referrer"は付けない。
  // 委譲先は親のoriginをlocation.ancestorOriginsで確かめており、
  // 付けると仕様上その値が"null"に置き換わって、更新が一度も完了しなくなるため
  iframe.style.display = 'none';

  const handlers: { timer?: ReturnType<typeof setTimeout>; onMessage?: (event: MessageEvent) => void } = {};
  const settle = (result: WellnessCloudRefreshResult): void => {
    if (handlers.timer) {
      clearTimeout(handlers.timer);
    }
    if (handlers.onMessage) {
      window.removeEventListener('message', handlers.onMessage);
    }
    iframe.remove();
    resolve(result);
  };
  handlers.onMessage = (event: MessageEvent): void => {
    const result = toRefreshResult(event, iframe.contentWindow);
    if (result) {
      settle(result);
    }
  };
  window.addEventListener('message', handlers.onMessage);
  handlers.timer = setTimeout(() => settle({ status: 'timeout' }), REFRESH_TIMEOUT_MS);
  document.body.appendChild(iframe);
});

export const refreshWellnessCloudSession = (expectedSub: string | null): Promise<WellnessCloudRefreshResult> => {
  if (pendingRefresh) {
    return pendingRefresh;
  }
  markWellnessCloudRefreshed();
  pendingRefresh = refreshInIframe().then((result) => {
    pendingRefresh = null;
    if (result.status === 'failure') {
      // 委譲先は失敗の理由を返さないが、大半はリフレッシュトークンの失効で、委譲先がトップレベルで認証し直すまで
      // 更新しても失敗し続ける。想定内の失敗なのでSentryにも送らない
      localStorage.setItem(REFRESH_SUSPENDED_STORAGE_KEY, 'true');
    } else if (result.status === 'timeout') {
      Sentry.captureMessage('WellnessCloudのセッション更新が時間内に応答しない');
    } else if (result.sub && expectedSub && result.sub !== expectedSub) {
      localStorage.setItem(USER_MISMATCH_STORAGE_KEY, 'true');
    }
    return result;
  });
  return pendingRefresh;
};

// 委譲先がトップレベルで認証し直す時点(ログイン時、委譲時)で更新を再開する
export const resumeWellnessCloudRefresh = (): void => {
  localStorage.removeItem(REFRESH_SUSPENDED_STORAGE_KEY);
};

export const waitForWellnessCloudRefresh = async (timeoutMs: number): Promise<void> => {
  if (!pendingRefresh) {
    return;
  }
  await Promise.race([
    pendingRefresh,
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
};

// 食い違いへの対処(ログアウト)は問診を開くときだけ行う。操作中に遷移すると入力中の内容が消えるため
export const consumeWellnessCloudUserMismatch = (): boolean => {
  if (localStorage.getItem(USER_MISMATCH_STORAGE_KEY) !== 'true') {
    return false;
  }
  localStorage.removeItem(USER_MISMATCH_STORAGE_KEY);
  return true;
};

export const resetWellnessCloudUserMismatch = (): void => {
  localStorage.removeItem(USER_MISMATCH_STORAGE_KEY);
};
